let placesLoader: Promise<typeof google> | null = null;

const API_KEY = import.meta.env.VITE_FRONTEND_FORGE_API_KEY;
const FORGE_BASE_URL = import.meta.env.VITE_FRONTEND_FORGE_API_URL || "https://forge.butterfly-effect.dev";
const MAPS_PROXY_URL = `${FORGE_BASE_URL}/v1/maps/proxy`;

export type SelectedAddress = {
  formattedAddress: string;
  city: string;
  county: string;
  zip: string;
};

function loadPlacesLibrary() {
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (placesLoader) return placesLoader;

  placesLoader = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-premier-places="true"]');
    if (existing) {
      existing.addEventListener("load", () => window.google?.maps?.places ? resolve(window.google) : reject(new Error("Google Places did not initialize")), { once: true });
      existing.addEventListener("error", () => reject(new Error("Google Places could not load")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.dataset.premierPlaces = "true";
    script.src = `${MAPS_PROXY_URL}/maps/api/js?key=${API_KEY}&v=weekly&libraries=places`;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = () => window.google?.maps?.places ? resolve(window.google) : reject(new Error("Google Places did not initialize"));
    script.onerror = () => reject(new Error("Google Places could not load"));
    document.head.appendChild(script);
  });

  return placesLoader;
}

function addressPart(parts: google.maps.GeocoderAddressComponent[] | undefined, type: string) {
  return parts?.find(part => part.types.includes(type))?.long_name ?? "";
}

export async function enableAddressAutocomplete(input: HTMLInputElement, onSelect: (address: SelectedAddress) => void) {
  try {
    const maps = await loadPlacesLibrary();
    if (!maps.maps.places || input.dataset.placesBound === "true") return;

    const autocomplete = new maps.maps.places.Autocomplete(input, {
      componentRestrictions: { country: "us" },
      fields: ["address_components", "formatted_address"],
      bounds: { north: 31.2, south: 24.3, east: -79.7, west: -87.7 },
      strictBounds: false,
      types: ["address"],
    });
    input.dataset.placesBound = "true";
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.formatted_address) return;
      onSelect({
        formattedAddress: place.formatted_address,
        city: addressPart(place.address_components, "locality"),
        county: addressPart(place.address_components, "administrative_area_level_2"),
        zip: addressPart(place.address_components, "postal_code"),
      });
    });
  } catch {
    // The address field intentionally remains a normal text input when Places is unavailable.
  }
}
